import { Component, Input, OnInit, inject } from "@angular/core";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { TicketStore } from "@app/services/store/ticket-store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { Job } from "@app/models/rest/job";
import { Ticket, Ticket_DetailProjection } from "@app/models/rest/ticket";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import {
  ColumnConfig,
  DateColumnConfig,
  FilterConfig,
  RowExpanderConfig
} from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Store as BryntumStore
} from "@bryntum/core-thin";
import { PriorityStore } from "@app/services/store/priority-store";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import { SkillProfileStore } from "@app/services/store/skill-profile-store";
import { TicketCompletionCodeStore } from "@app/services/store/ticket-completion-code-store";
import { ZoneStore } from "@app/services/store/zone-store";
import { GridComponent } from "../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";

// TODO: Move to shared module
@Component({
  selector: "app-ticket-table-viewer",
  templateUrl: "./ticket-table-viewer.component.html",
  styleUrls: ["./ticket-table-viewer.component.scss"],
  imports: [GridComponent, MatPaginator]
})
export class TicketTableViewerComponent
  extends BaseDataTablePageDirective<Ticket>
  implements OnInit
{
  protected ticketStore: TicketStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  protected priorityStore = inject(PriorityStore);
  protected visitTypeStore = inject(VisitTypeStore);
  protected skillProfileStore = inject(SkillProfileStore);
  protected completionCodeStore = inject(TicketCompletionCodeStore);
  protected zoneStore = inject(ZoneStore);

  public rowExpanderConfig: Partial<RowExpanderConfig> = {
    columnPosition: "last",
    renderer: async ({ record }) => {
      console.log("Rendering visit card for record", record);
      const { id } = record.get("visit");
      console.log("Extracted ID", id);
      return `<visit-card-component visit-id="${id}" style="width:100%"></visit-card-component>`;
    }
  };

  @Input()
  public columns: (ColumnConfig | DateColumnConfig)[] = [
    {
      field: "visit.job.externalJobReference",
      text: "Job",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.job.externalPropertyReference",
      text: "Property",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "duration",
      text: "Duration (mins)",
      flex: 1,
      editor: false,
      sortable: true,
      filterable: true
    },
    {
      field: "skillProfile.description",
      text: "Skill Profile",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "ticketStatus",
      text: "Status",
      flex: 1,
      editor: false
    },
    {
      field: "ticketCompletionCode.code",
      text: "Completion Code",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "followOnRequired",
      text: "Follow On Required",
      flex: 1,
      editor: false,
      filterable: {
        filterField: {
          type: "combo",
          multiSelect: true,
          valueField: "value",
          displayField: "text",
          placeholder: "Filter follow on required...",
          store: {
            data: [
              { value: "true", text: "Yes" },
              { value: "false", text: "No" },
              { value: null, text: "Not Set" }
            ]
          }
        }
      },
      renderer: ({ value }) => {
        if (value === null || value === undefined) return "Not Set";
        if (value === "true" || value === true) return "Yes";
        if (value === "false" || value === false) return "No";
        return "Not Set";
      }
    },
    {
      field: "followOnActioned",
      text: "Follow On Actioned",
      flex: 1,
      editor: false,
      filterable: {
        filterField: {
          type: "combo",
          multiSelect: true,
          valueField: "value",
          displayField: "text",
          placeholder: "Filter follow on actioned...",
          store: {
            data: [
              { value: "true", text: "Yes" },
              { value: "false", text: "No" },
              { value: null, text: "Not Set" }
            ]
          }
        }
      },
      renderer: ({ value }) => {
        if (value === null || value === undefined) return "Not Set";
        if (value === "true" || value === true) return "Yes";
        if (value === "false" || value === false) return "No";
        return "Not Set";
      }
    },
    {
      field: "despatchedTime",
      text: "Despatched",
      format: "DD/MM/YYYY HH:mm",

      flex: 1,
      type: "date",
      editor: false
    },
    {
      field: "enrouteTime",
      text: "En Route",
      format: "DD/MM/YYYY HH:mm",

      flex: 1,
      type: "date",
      editor: false
    },
    {
      field: "arrivedTime",
      text: "Arrived",
      format: "DD/MM/YYYY HH:mm",

      flex: 1,
      type: "date",
      editor: false
    },
    {
      field: "startedTime",
      text: "Started",
      format: "DD/MM/YYYY HH:mm",

      flex: 1,
      type: "date",
      editor: false
    },
    {
      field: "completedTime",
      text: "Completed",
      format: "DD/MM/YYYY HH:mm",

      flex: 1,
      type: "date",
      editor: false
    },
    {
      field: "resourceAvailability.resource.name",
      text: "Resource",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.job.zone.name",
      text: "Zone",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.visitStatus",
      text: "Visit Status",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.visitType.description",
      text: "Type",
      filterable: true,
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.appointmentStart",
      text: "Appointment Start",
      flex: 1,
      format: "DD/MM/YYYY HH:mm",
      type: "date",
      editor: false,
      sortable: true
    },
    {
      field: "visit.appointmentEnd",
      text: "Appointment End",
      flex: 1,
      format: "DD/MM/YYYY HH:mm",
      type: "date",
      editor: false,
      sortable: true
    },

    {
      field: "visit.job.planningWindowEnd",
      text: "Planning Window End",
      flex: 1,
      format: "DD/MM/YYYY HH:mm",
      type: "date",
      editor: false,
      sortable: true
    },
    {
      field: "visit.job.postCode",
      text: "PostCode",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visit.job.priority.description",
      text: "Priority",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      type: "ticketDialog",
      exportable: false,
      text: "Pop-up",
      width: 70
    } as unknown as ColumnConfig
  ];

  fakeTicketStore = new BryntumStore({
    fields: ["id", "name", "status"],
    data: [
      {
        id: 1,
        externalJobReference: "Job 1",
        externalProjectReference: "sample",
        visit: { visitStatus: "NEW" },
        postCode: "BN1 1AA",
        followOnRequired: "true",
        followOnActioned: "false"
      },
      {
        id: 2,
        name: "Job 2",
        visit: { visitStatus: "REFERRED" },
        followOnRequired: "false",
        followOnActioned: "false"
      },
      {
        id: 3,
        name: "Job 3",
        visit: { visitStatus: "BOOKED" },
        followOnRequired: "true",
        followOnActioned: "true"
      },
      {
        id: 4,
        name: "Job 4",
        visit: { visitStatus: "IN_PROGRESS" },
        followOnRequired: "false",
        followOnActioned: "false"
      },
      {
        id: 5,
        name: "Job 6",
        visit: { visitStatus: "CANCELLED" },
        followOnRequired: "true",
        followOnActioned: "false"
      },
      {
        id: 6,
        name: "Job 7",
        visit: { visitStatus: "COMPLETED" },
        followOnRequired: "false",
        followOnActioned: "true"
      },
      {
        id: 7,
        ticketStatus: "PLANNING",
        followOnRequired: null,
        followOnActioned: null
      },
      {
        id: 8,
        ticketStatus: "DESPATCHED",
        followOnRequired: "true",
        followOnActioned: "false"
      },
      {
        id: 9,
        ticketStatus: "ENROUTE",
        followOnRequired: "false",
        followOnActioned: "false"
      },
      {
        id: 10,
        ticketStatus: "ARRIVED",
        followOnRequired: "true",
        followOnActioned: "true"
      },
      {
        id: 11,
        ticketStatus: "STARTED",
        followOnRequired: "false",
        followOnActioned: "false"
      },
      {
        id: 12,
        ticketStatus: "COMPLETE",
        followOnRequired: "true",
        followOnActioned: "true"
      }
    ]
  });

  // statusFilterCreated: boolean = false;
  filterConfig: FilterConfig = {
    pickerConfig: {
      showAddFilterButton: false,
      dateFormat: "DD/MM/YYYY",

      canDeleteFilter: (filter) => {
        return filter.property !== "status";
      },
      getFieldFilterPickerConfig: (
        collectionFilterConfig: CollectionFilterConfig
      ): Partial<FieldFilterPickerConfig> => {
        switch (collectionFilterConfig.property) {
          case "visit.appointmentStart":
          case "visit.appointmentEnd":
          case "visit.job.planningWindowEnd":
          case "enrouteTime":
          case "arrivedTime":
          case "startedTime":
          case "completedTime":
            return {
              dateFormat: "DD/MM/YYYY",
              getValueFieldConfig: (filter, fieldConfig) => {
                console.log(filter, fieldConfig);
                fieldConfig.type = "date";
                fieldConfig.format = "DD/MM/YYYY";
                return fieldConfig;
              },
              operators: this.dateOperators
            };
          case "visit.job.externalPropertyReference":
          case "visit.job.externalProjectReference":
          case "visit.job.externalCaseReference":
          case "visit.job.externalJobReference":
          case "visit.job.postCode":
          case "resourceAvailability.resource.name":
            let stringFieldResult: any = {
              store: this.fakeTicketStore,
              operators: this.stringOperators
            };
            if (
              this.gridStore.filters.findItem(
                "property",
                collectionFilterConfig.property
              ) === null
            ) {
              stringFieldResult.filter = {
                property: collectionFilterConfig.property,
                operator:
                  collectionFilterConfig.operator === "includes"
                    ? "*"
                    : collectionFilterConfig.operator,
                value: collectionFilterConfig.value
              };
            }
            return stringFieldResult;
          case "visit.job.priority.description":
          case "visit.visitType.description":
          case "visit.visitStatus":
          case "skillProfile.description":
          case "ticketStatus":
          case "ticketCompletionCode.code":
          case "visit.job.zone.name":
            const fixedFieldResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              store: this.fakeTicketStore,
              operators: this.fixedSetOperators,
              getValueFieldConfig: (filter, fieldConfig) => {
                fieldConfig.createOnUnmatched = false;
                return fieldConfig;
              }
            };
            if (
              this.gridStore.filters.findItem(
                "property",
                collectionFilterConfig.property
              ) === null
            ) {
              fixedFieldResult.filter = {
                id: `${collectionFilterConfig.property}-isIncludedIn`,
                property: collectionFilterConfig.property,
                operator: "isIncludedIn",
                value: collectionFilterConfig.value,
                disabled: collectionFilterConfig.disabled
              };
            }
            return fixedFieldResult;
        }
        return {};
      }
    }
  };

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const ticketStore = inject(TicketStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      ticketStore,
      store,
      dialog,
      "Ticket",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.ticketStore = ticketStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  ngOnInit() {
    super.ngOnInit();

    this.priorityStore.loadAll().then((value) => {
      value.resources.forEach((priority, index) => {
        this.fakeTicketStore.add({
          id: index + 2000,
          name: priority.description,
          visit: {
            job: {
              priority: priority
            }
          }
        });
      });
    });

    this.visitTypeStore.loadAll().then((value) => {
      value.resources.forEach((visitType, index) => {
        this.fakeTicketStore.add({
          id: index + 3000,
          name: visitType.description,
          visit: {
            visitType: visitType
          }
        });
      });
    });

    this.skillProfileStore.loadAll().then((value) => {
      value.resources.forEach((skillProfile, index) => {
        this.fakeTicketStore.add({
          id: index + 4000,
          name: skillProfile.description,
          skillProfile: skillProfile
        });
      });
    });

    this.completionCodeStore.loadAll().then((value) => {
      value.resources.forEach((completionCode, index) => {
        this.fakeTicketStore.add({
          id: index + 5000,
          name: completionCode.code,
          ticketCompletionCode: completionCode
        });
      });
    });

    this.zoneStore.loadAll().then((value) => {
      value.resources.forEach((zone, index) => {
        this.fakeTicketStore.add({
          id: index + 6000,
          name: zone.name,
          visit: {
            job: {
              zone: zone
            }
          }
        });
      });
    });
  }

  openJob(ticket: Ticket_DetailProjection) {
    this.router.navigate(["/jobs", "detail", ticket.visit.job.id]).then();
  }

  ticketDialog(ticket: Ticket) {
    this.dialog.open(TicketActionDialogComponent, {
      hasBackdrop: true,
      autoFocus: false,
      width: "500px",
      panelClass: "toolbar-popup-dialog",
      data: {
        ticketId: ticket.id
      }
    });
  }
}
